import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { isMobile } from '../../constants/constants';
import { convertNumberToHMS } from '../Admin/globalFunctions';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import MikaFileManager from '../../components/MikaFileManager/MikaFileManager';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import { axiosCaller } from '../../utils/apiCaller';
import { SelectOption } from '../../types/selectOption.type';
import '../Contact/Contact.scss';
import { formatPrice } from '../../utils/react';
import ArchivedProductCardGrid from './ArchivedProductCardGrid';

interface Props {
    history: any;
    location: any;
    actions: any;
    productionActions: any;
    production: any;
    intl: any;
    permissions: any;
    buttonLoader: boolean;
    departments: SelectOption[];
    loading: any;
    archivedProducts: any;
    archivedProductsPagination: any;
    archivedProductsGrandTotal: number;
}

const VIEW_STORAGE_KEY = 'archivedProductsViewMode';

const ArchivedProductsPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const ARCHIVE_STRIP_FILTERS = useMemo(() => [
        { value: 'thisMonth', label: fm('production.productArchive.strip.thisMonth', 'Këtë muaj') },
        { value: 'onTime',    label: fm('production.productArchive.strip.onTime', 'Në kohë') },
        { value: 'late',      label: fm('production.dashboard.lateProducts', 'Me vonesë') },
    ], [fm]);

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        deptIds: { default: '', type: 'string' },
        stripFilters: { default: '', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        sortBy: { default: '', type: 'string' },
        sortDir: { default: '', type: 'string' },
    });

    // Derived variables
    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const selectedDepartmentIds: number[] = filters.deptIds ? filters.deptIds.split(',').map(Number) : [];
    const selectedStripFilters: string[] = filters.stripFilters ? filters.stripFilters.split(',') : [];
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const sortBy: string | null = filters.sortBy || null;
    const sortDir: 'asc' | 'desc' | null = filters.sortDir === 'asc' || filters.sortDir === 'desc' ? filters.sortDir : null;

    // Setter wrappers
    const setCurrentPage = (p: number) => setFilter('page', p);
    const setDateRange = (r: DateRange | null) => {
        setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 });
    };

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    // Dashboard stats
    const [statsData, setStatsData] = useState<any>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    // Modal state
    const [selectedProduct, setSelectedProduct] = useState<any>(null);

    // Data
    const [transformedProducts, setTransformedProducts] = useState<any[]>([]);
    const [totalPrice, setTotalPrice] = useState(0);

    const loadStats = () => {
        props.productionActions.getArchivedProductsDashboardStats().then((res: any) => {
            if (res) setStatsData(res);
        });
    };

    useEffect(() => {
        props.actions.getAllDepartments();
        loadStats();
    }, []);

    const getExtraFilters = () => {
        const extra: any = {};
        if (selectedStripFilters.includes('thisMonth')) extra.thisMonth = true;
        if (selectedStripFilters.includes('onTime')) extra.onTime = true;
        if (selectedStripFilters.includes('late')) extra.late = true;
        if (sortBy) extra.sortBy = sortBy;
        if (sortDir) extra.sortDir = sortDir;
        return extra;
    };

    const handleSortChange = (key: string | null, dir: 'asc' | 'desc' | null) => {
        setFilters({ sortBy: key || '', sortDir: dir || '', page: 1 });
    };

    useEffect(() => {
        fetchData();
    }, [props.location.search]);

    const fetchData = () => {
        const deptIds = selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null;
        props.actions.getArchivedProducts(
            deptIds, null,
            dateRange?.from || null, dateRange?.to || null,
            currentPage, perPage, searchTerm, getExtraFilters()
        );
    };

    const handleSearch = (term: string) => {
        replaceFilters({ search: term, page: 1 });
    };

    // Transform API data
    useEffect(() => {
        const products = props.archivedProducts || [];
        const transformed = products.map((p: any) => ({
            clientName: p?.client?.name,
            projectName: p?.project?.name,
            productName: p?.designation?.name ? `${p.designation.name}${p.type ? ` (${p.type})` : ''}` : '\u2014',
            trackersStart: p?.trackersStart,
            trackersEnd: p?.trackersEnd,
            duration: p?.totalDuration ? convertNumberToHMS(p.totalDuration) : '00h:00m:00s',
            quantity: p?.quantity,
            price: p?.discountPrice || p?.totalPrice,
            files: (p?.productFiles || []).map((f: any) => ({
                id: f.id,
                name: f.nameOfBlueprint || 'file',
                url: f.blueprint,
            })),
        }));
        setTransformedProducts(transformed);
        setTotalPrice(Number(props.archivedProductsGrandTotal) || 0);
    }, [props.archivedProducts, props.archivedProductsGrandTotal]);

    // Dashboard strip
    const dashboardStats = useMemo(() => {
        const s = statsData;
        return [
            { key: 'total',       label: fm('production.productArchive.strip.total', 'Total'),                 value: s?.total ?? 0,           icon: 'users' },
            { key: 'thisMonth',   label: fm('production.productArchive.strip.thisMonth', 'Këtë muaj'),         value: s?.thisMonth ?? '\u2014', icon: 'folder' },
            { key: 'onTime',      label: fm('production.productArchive.strip.onTime', 'Në kohë'),              value: s?.onTime ?? '\u2014',    icon: 'check' },
            { key: 'late',        label: fm('production.dashboard.lateProducts', 'Me vonesë'),                 value: s?.late ?? '\u2014',      icon: 'alert' },
            { key: 'departments', label: fm('production.dashboard.departments', 'Departamentet'),              value: selectedDepartmentIds.length > 0 ? selectedDepartmentIds.length : (props.departments || []).length, icon: 'layers' },
        ];
    }, [statsData, props.departments, selectedDepartmentIds, fm]);

    // Department popover
    const [deptPopoverOpen, setDeptPopoverOpen] = useState(false);
    const deptRef = useRef<HTMLDivElement>(null);
    const [deptPopoverPos, setDeptPopoverPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (deptRef.current && !deptRef.current.contains(e.target as Node)) setDeptPopoverOpen(false);
        };
        if (deptPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [deptPopoverOpen]);

    const toggleDeptFilter = (deptId: number) => {
        const next = selectedDepartmentIds.includes(deptId) ? selectedDepartmentIds.filter(id => id !== deptId) : [...selectedDepartmentIds, deptId];
        setFilters({ deptIds: next.join(','), page: 1 });
    };

    const handleStripClick = (key: string) => {
        if (key === 'total') {
            setFilters({ stripFilters: '', deptIds: '', page: 1 });
            setDeptPopoverOpen(false);
            return;
        }
        if (key === 'departments') {
            if (!deptPopoverOpen && stripRef.current) {
                const cards = stripRef.current.querySelectorAll('.cds-card');
                const card = cards[cards.length - 1];
                if (card) {
                    const rect = card.getBoundingClientRect();
                    setDeptPopoverPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
                }
            }
            setDeptPopoverOpen(!deptPopoverOpen);
            return;
        }
        const next = selectedStripFilters.includes(key) ? selectedStripFilters.filter(k => k !== key) : [...selectedStripFilters, key];
        setFilters({ stripFilters: next.join(','), page: 1 });
    };

    // Compute active strip keys for highlighting
    const activeStripKeys = useMemo(() => {
        const keys: string[] = [...selectedStripFilters];
        if (selectedDepartmentIds.length > 0) keys.push('departments');
        return keys.length > 0 ? keys : ['total'];
    }, [selectedStripFilters, selectedDepartmentIds]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    // Columns
    const columns: MikaColumn[] = useMemo(() => [
        { key: 'clientName',    title: fm('production.detail.client', 'Klienti'),                    sortable: true, render: (val: any) => val || '\u2014' },
        { key: 'projectName',   title: fm('production.detail.project', 'Projekti'),                  hideOnMobile: true },
        { key: 'productName',   title: fm('production.board.productCol', 'Produkti'),                render: (val: any) => val || '\u2014' },
        { key: 'trackersStart', title: fm('production.productArchive.col.processStart', 'Fillimi procesit'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'trackersEnd',   title: fm('production.productArchive.col.processEnd', 'Mbarimi procesit'),   sortable: true, hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: 'duration', title: fm('production.productArchive.col.duration', 'Koha'), hideOnMobile: true,
            render: (val: any) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{val || '\u2014'}</span>
        },
        { key: 'quantity', title: fm('production.detail.quantity', 'Sasia'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: 'price', title: fm('production.detail.value', 'Vlera'), sortable: true, hideOnMobile: true,
            render: (val: any) => val ? formatPrice(val, '\u20AC') : '\u2014'
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const openFilesModal = (row: any) => {
        if (!row.files || row.files.length === 0) {
            toast.info(fm('production.productArchive.noFiles', 'Ky produkt nuk ka skedarë!'));
            return;
        }
        setSelectedProduct(row);
    };

    const tableActions = [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
            tooltip: fm('production.productArchive.tooltip.viewFiles', 'Skedarët'),
            onClick: (row: any) => openFilesModal(row)
        }
    ];

    const closeModal = () => setSelectedProduct(null);

    const pagination = props.archivedProductsPagination;

    // Export
    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'clientName',    title: fm('production.detail.client', 'Klienti') },
        { key: 'projectName',   title: fm('production.detail.project', 'Projekti') },
        { key: 'productName',   title: fm('production.board.productCol', 'Produkti') },
        { key: 'trackersStart', title: fm('production.productArchive.col.processStart', 'Fillimi procesit') },
        { key: 'trackersEnd',   title: fm('production.productArchive.col.processEnd', 'Mbarimi procesit') },
        { key: 'duration',      title: fm('production.productArchive.col.duration', 'Koha') },
        { key: 'quantity',      title: fm('production.detail.quantity', 'Sasia') },
        { key: 'price',         title: fm('production.detail.value', 'Vlera'), render: (r) => r.price ? formatPrice(r.price) : '' },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async (): Promise<any[]> => {
        let queryParams: string[] = ['active=1', 'done=1', 'per_page=0'];
        if (selectedDepartmentIds.length > 0) selectedDepartmentIds.forEach(id => queryParams.push(`departmentIds[]=${id}`));
        if (dateRange?.from) queryParams.push(`start=${dateRange.from}`);
        if (dateRange?.to) queryParams.push(`end=${dateRange.to}`);
        if (searchTerm) queryParams.push(`search=${encodeURIComponent(searchTerm)}`);
        const extra = getExtraFilters();
        if (extra.thisMonth) queryParams.push('thisMonth=1');
        if (extra.onTime) queryParams.push('onTime=1');
        if (extra.late) queryParams.push('late=1');
        if (extra.sortBy) queryParams.push(`sortBy=${encodeURIComponent(extra.sortBy)}`);
        if (extra.sortDir) queryParams.push(`sortDir=${encodeURIComponent(extra.sortDir)}`);
        const res = await axiosCaller('GET', `products/product-archive?${queryParams.join('&')}`);
        const raw = res?.data?.data || [];
        return raw.map((p: any) => ({
            clientName: p?.client?.name,
            projectName: p?.project?.name,
            productName: p?.designation?.name ? `${p.designation.name}${p.type ? ` (${p.type})` : ''}` : '',
            trackersStart: p?.trackersStart,
            trackersEnd: p?.trackersEnd,
            duration: p?.totalDuration ? convertNumberToHMS(p.totalDuration) : '00h:00m:00s',
            quantity: p?.quantity,
            price: p?.discountPrice || p?.totalPrice,
        }));
    };

    return (
        <div className="clp-page">
            <MikaPageHeader
                title={<>{fm('production.productArchive.title', 'Arkiva e produkteve')} {props.permissions?.fullPrivileges && <span style={{ fontSize: 18, fontWeight: 700, color: '#22c55e', marginLeft: 12 }}>{formatPrice(totalPrice)}€</span>}</>}
            />

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.productArchive.searchPlaceholder', 'Kërko produkte...')}
                        value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('production.productArchive.exportFilename', 'arkiva_produkteve')} title={fm('production.productArchive.title', 'Arkiva e produkteve')} companyProfile={(props as any).companyProfile}
                        sumColumn={{ getValue: (row) => row.price ? Number(row.price) : 0, label: fm('production.products.exportSumLabel', 'Totali:'), suffix: '\u20AC' }}
                    />
                </div>
            </div>

            <div ref={stripRef} style={{ position: 'relative' }}>
                <ContactDashboardStrip
                    stats={dashboardStats}
                    activeFilter={activeStripKeys}
                    onFilterClick={handleStripClick}
                />
            </div>
            {deptPopoverOpen && (
                <div ref={deptRef} className="clp-city-popover" style={{ top: deptPopoverPos.top, left: deptPopoverPos.left, transform: 'translateX(-50%)' }}>
                    <div className="clp-city-header"><FormattedMessage id="production.dashboard.departments" defaultMessage="Departamentet" /></div>
                    {(props.departments || []).map((dept: any) => (
                        <button
                            key={dept.value}
                            className={`clp-city-row ${selectedDepartmentIds.includes(dept.value) ? 'clp-city-row-active' : ''}`}
                            onClick={() => toggleDeptFilter(dept.value)}
                            type="button"
                        >
                            <span className="clp-city-name">{dept.label}</span>
                            {selectedDepartmentIds.includes(dept.value) && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Active filter chips — below strip */}
            {(selectedDepartmentIds.length > 0 || selectedStripFilters.length > 0) && (
                <div className="clp-active-filter-bar" style={{ flexWrap: 'wrap', gap: 6 }}>
                    {selectedDepartmentIds.map(id => {
                        const dept = (props.departments || []).find((d: any) => d.value === id);
                        return dept ? (
                            <div key={`dept-${id}`} className="clp-active-filter-chip">
                                <span>{dept.label}</span>
                                <button className="clp-active-filter-clear" onClick={() => { const next = selectedDepartmentIds.filter(v => v !== id); setFilters({ deptIds: next.join(','), page: 1 }); }} type="button">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                        ) : null;
                    })}
                    {selectedStripFilters.map(key => {
                        const opt = ARCHIVE_STRIP_FILTERS.find(f => f.value === key);
                        return opt ? (
                            <div key={`strip-${key}`} className="clp-active-filter-chip">
                                <span>{opt.label}</span>
                                <button className="clp-active-filter-clear" onClick={() => { const next = selectedStripFilters.filter(v => v !== key); setFilters({ stripFilters: next.join(','), page: 1 }); }} type="button">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                        ) : null;
                    })}
                </div>
            )}

            {effectiveView === 'table'
                ? <MikaTable
                    columns={columns}
                    data={transformedProducts}
                    actions={tableActions}
                    loading={props.loading}
                    onRowClick={openFilesModal}
                    sortKey={sortBy}
                    sortDir={sortDir}
                    onSortChange={handleSortChange}
                />
                : <ArchivedProductCardGrid data={transformedProducts} onViewFiles={openFilesModal} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setFilter('page', p)} onPerPageChange={(v) => setFilters({ perPage: v, page: 1 })}
                label={fm('production.productArchive.recordsLabel', 'produkte të arkivuara')}
            />

            {/* Files modal */}
            {selectedProduct && (
                <div className="agr-modal-overlay" onClick={closeModal}>
                    <div className="agr-modal arch-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="agr-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>{selectedProduct.productName}</span>
                            <button onClick={closeModal} type="button" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>&times;</button>
                        </div>
                        <div className="agr-modal-body" style={{ overflowY: 'auto', padding: 0 }}>
                            {/* Info grid */}
                            <div className="arch-info-grid">
                                <div className="arch-info-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                    <div>
                                        <span className="arch-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>
                                        <span className="arch-info-value">{selectedProduct.clientName}</span>
                                    </div>
                                </div>
                                <div className="arch-info-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>
                                    <div>
                                        <span className="arch-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                        <span className="arch-info-value">{selectedProduct.projectName}</span>
                                    </div>
                                </div>
                                {selectedProduct.duration && (
                                    <div className="arch-info-item">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                        <div>
                                            <span className="arch-info-label"><FormattedMessage id="production.productArchive.modal.duration" defaultMessage="Kohëzgjatja" /></span>
                                            <span className="arch-info-value" style={{ fontFamily: 'monospace' }}>{selectedProduct.duration}</span>
                                        </div>
                                    </div>
                                )}
                                {selectedProduct.price && (
                                    <div className="arch-info-item">
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#64748b', width: 14, textAlign: 'center', flexShrink: 0 }}>&euro;</span>
                                        <div>
                                            <span className="arch-info-label"><FormattedMessage id="production.detail.value" defaultMessage="Vlera" /></span>
                                            <span className="arch-info-value" style={{ color: '#1e40af', fontWeight: 600 }}>{formatPrice(selectedProduct.price, '€')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Files section */}
                            <div className="arch-files-section">
                                <MikaFileManager files={selectedProduct.files} />
                            </div>
                        </div>

                        <div className="agr-modal-actions">
                            <button className="agr-btn-secondary" onClick={closeModal} type="button"><FormattedMessage id="production.productArchive.modal.close" defaultMessage="Mbyll" /></button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        production: state.production,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        productionActions: bindActionCreators(productionActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ArchivedProductsPage as any)));
